import { Alert, List, ListItem, ListItemText, Box, Avatar, Typography, Divider, Paper, Container, Button, TablePagination, Stack, TextField, IconButton, ToggleButton, ToggleButtonGroup, Snackbar, Collapse } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Grid from '@mui/material/Grid2';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useContext, useEffect, useState } from "react";
import { AuthContext, FireactContext, SetPageTitle } from "../../lib/core";
import { useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { collection, query, where, and, or, getDocs, getDoc, doc, setDoc, addDoc, deleteDoc, GeoPoint, orderBy } from 'firebase/firestore';
import { getAuth, sendEmailVerification } from "firebase/auth";
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {AdminPageContext} from "./AdminPageContext";

export const AdminEarnings = () => {
    const auth = getAuth();
    const { authInstance, firestoreInstance, functionsInstance } = useContext(AuthContext);
    const navigate = useNavigate();
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [prompt, setPrompt] = useState(null);
    const [processingId, setProcessingId] = useState(null);
    const [processedRequests, setProcessedRequests] = useState([]);
    const { authUser } = useContext(AuthContext);

    const { admin } = useContext(AdminPageContext);

    const [totalPayoutRequests, setTotalPayoutRequests] = useState(null);
    const [pendingRequests, setPendingRequests] = useState(null);
    const [completedRequests, setCompletedRequests] = useState(null);
    const [pendingRequestsList, setPendingRequestsList] = useState([]);
    const [completedRequestsList, setCompletedRequestsList] = useState([]);
    const [showPendingList, setShowPendingList] = useState(false);
    const [showCompletedList, setShowCompletedList] = useState(false);
    const [pendingUsers, setPendingUsers] = useState({});
    const [completedUsers, setCompletedUsers] = useState({});

    // Helper to format Firestore Timestamp, null, or fallback
    function formatRequestedAt(requestedAt) {
        // Debug: log the requestedAt object to inspect its structure
        console.log('DEBUG requestedAt:', requestedAt);
        if (!requestedAt) return 'N/A';
        // Firestore Timestamp object
        if (requestedAt.toDate) {
            try {
                return requestedAt.toDate().toLocaleString();
            } catch {
                return 'Invalid date';
            }
        }
        // If it's a Firestore timestamp-like object from JSON
        if (typeof requestedAt === 'object') {
            if (requestedAt.seconds) {
                try {
                    return new Date(requestedAt.seconds * 1000).toLocaleString();
                } catch {
                    return 'Invalid date';
                }
            }
            if (requestedAt._seconds) {
                try {
                    return new Date(requestedAt._seconds * 1000).toLocaleString();
                } catch {
                    return 'Invalid date';
                }
            }
        }
        // If it's a JS Date
        if (requestedAt instanceof Date) {
            return requestedAt.toLocaleString();
        }
        // If it's a string
        if (typeof requestedAt === 'string') {
            return requestedAt;
        }
        return 'Unknown';
    }

    useEffect(() => {
        async function fetchPayoutRequests() {
            if (admin === 'super' && functionsInstance) {
                try {
                    const adminListPayoutRequests = httpsCallable(functionsInstance, 'adminListPayoutRequests');
                    const result = await adminListPayoutRequests();
                    // payoutRequests should be an array of { id, ...data }
                    let payoutRequests = result.data.payoutRequests;
                    // If payoutRequests are missing 'id', try to map from Firestore response
                    if (Array.isArray(payoutRequests) && payoutRequests.length > 0 && !('id' in payoutRequests[0])) {
                        // Try to get id from payoutRequestId or other unique field
                        payoutRequests = payoutRequests.map(pr => ({
                            ...pr,
                            id: pr.payoutRequestId || pr.id || pr.docId || pr._id || pr.channelId + '-' + pr.userId // fallback
                        }));
                    }
                    setTotalPayoutRequests(payoutRequests.length);
                    const pendingList = payoutRequests.filter(pr => pr.status === 'pending');
                    setPendingRequests(pendingList.length);
                    setPendingRequestsList(pendingList);
                    const completedList = payoutRequests.filter(pr => pr.status === 'completed');
                    setCompletedRequests(completedList.length);
                    setCompletedRequestsList(completedList);
                } catch (err) {
                    setError(err.message || 'Failed to fetch payout requests.');
                }
            }
            setLoaded(true);
        }
        fetchPayoutRequests();
    }, [auth.currentUser.uid, admin, functionsInstance]);

    // Fetch user details for pending and completed requests when list is shown
    useEffect(() => {
        async function fetchUsers() {
            // Pending users
            if (showPendingList && pendingRequestsList.length > 0 && firestoreInstance) {
                const usersMap = {};
                for (const pr of pendingRequestsList) {
                    if (pr.userId && !usersMap[pr.userId]) {
                        try {
                            const userDoc = await getDoc(doc(firestoreInstance, 'users', pr.userId));
                            if (userDoc.exists()) {
                                usersMap[pr.userId] = userDoc.data();
                            } else {
                                usersMap[pr.userId] = null;
                            }
                        } catch {
                            usersMap[pr.userId] = null;
                        }
                    }
                }
                setPendingUsers(usersMap);
            }
            // Completed users
            if (showCompletedList && completedRequestsList.length > 0 && firestoreInstance) {
                const completedUsersMap = {};
                for (const pr of completedRequestsList) {
                    if (pr.userId && !completedUsersMap[pr.userId]) {
                        try {
                            const userDoc = await getDoc(doc(firestoreInstance, 'users', pr.userId));
                            if (userDoc.exists()) {
                                completedUsersMap[pr.userId] = userDoc.data();
                            } else {
                                completedUsersMap[pr.userId] = null;
                            }
                        } catch {
                            completedUsersMap[pr.userId] = null;
                        }
                    }
                }
                setCompletedUsers(completedUsersMap);
            }
        }
        fetchUsers();
    }, [showPendingList, pendingRequestsList, showCompletedList, completedRequestsList, firestoreInstance]);

    // Handler for processing payout request
    const handleProcessRequest = async (payoutRequestId) => {
        if (!functionsInstance) return;
        setProcessingId(payoutRequestId);
        try {
            const adminProcessPayoutRequests = httpsCallable(functionsInstance, 'adminProcessPayoutRequests');
            const result = await adminProcessPayoutRequests({ payoutRequestId });
            if (result.data && result.data.success) {
                setPrompt('Payout request processed successfully.');
                setProcessedRequests(prev => [...prev, payoutRequestId]);
            } else {
                setError(result.data && result.data.message ? result.data.message : 'Failed to process payout request.');
            }
        } catch (err) {
            setError(err.message || 'Failed to process payout request.');
        }
        setProcessingId(null);
    };
    return (<>
        {loaded ? (
            <>
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={0}>
                    <Grid size={{ xs: 12, md: 12 }}>
                        {error !== null &&
                            <Alert severity="error">{error}</Alert>
                        }
                        {prompt !== null &&
                            <Alert severity="info">
                                {prompt}
                            </Alert>
                        }
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Box sx={{ flexGrow: 1, p: 0, width: '100%', backgroundColor: '#FFF'}} >
                            {admin == 'super' && (
                                <>
                                    <Paper elevation={3} sx={{ p: 3, borderRadius: 3, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', boxShadow: 3 }}>
                                        <Stack direction="row" alignItems="center" spacing={2}>
                                            <Box>
                                                <Typography variant="h5" fontWeight={700} color="#1976d2">
                                                    Website Admin Console - Payouts Management
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Welcome, Super Admin!
                                                </Typography>
                                            </Box>
                                        </Stack>
                                        <Divider sx={{ my: 2 }} />
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={4}>
                                                <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Total Payout Requests</Typography>
                                                    <Typography variant="h6" color="#1976d2">
                                                        {totalPayoutRequests !== null ? totalPayoutRequests : <CircularProgress size={20} />}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, cursor: 'pointer', transition: 'box-shadow 0.2s', boxShadow: showPendingList ? 6 : 2 }} onClick={() => setShowPendingList(!showPendingList)}>
                                                    <Typography variant="subtitle2" color="text.secondary">Pending Requests</Typography>
                                                    <Typography variant="h6" color="#f57c00">
                                                        {pendingRequests !== null ? pendingRequests : <CircularProgress size={20} />}
                                                    </Typography>
                                                    <Typography variant="caption" color="primary" sx={{ textDecoration: 'underline' }}>
                                                        {showPendingList ? 'Hide List' : 'Show List'}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                            <Grid item xs={12} md={4}>
                                                <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, cursor: 'pointer', transition: 'box-shadow 0.2s', boxShadow: showCompletedList ? 6 : 2 }} onClick={() => setShowCompletedList(!showCompletedList)}>
                                                    <Typography variant="subtitle2" color="text.secondary">Completed Requests</Typography>
                                                    <Typography variant="h6" color="#388e3c">
                                                        {completedRequests !== null ? completedRequests : <CircularProgress size={20} />}
                                                    </Typography>
                                                    <Typography variant="caption" color="primary" sx={{ textDecoration: 'underline' }}>
                                                        {showCompletedList ? 'Hide List' : 'Show List'}
                                                    </Typography>
                                                </Paper>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                    {showPendingList && (
                                        <Paper elevation={2} sx={{ mt: 2, p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle1" fontWeight={600} color="#f57c00" sx={{ mb: 1 }}>
                                                Pending Requests List
                                            </Typography>
                                            {pendingRequestsList.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">No pending requests.</Typography>
                                            ) : (
                                                <Grid container spacing={2}>
                                                    {pendingRequestsList.map((pr, idx) => {
                                                        const user = pendingUsers[pr.userId];
                                                        return (
                                                            <Grid item xs={12} md={4} key={pr.userId + '-' + idx}>
                                                                <Paper elevation={3} sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                                        {user && user.photoURL && (
                                                                            <Avatar src={user.photoURL} alt={user.displayName || user.email || pr.userId} sx={{ mr: 2 }} />
                                                                        )}
                                                                        <Box>
                                                                            <Typography variant="subtitle2" color="primary">{user ? (user.displayName || user.email || pr.userId) : `User: ${pr.userId}`}</Typography>
                                                                            {user && user.email && (
                                                                                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                                                            )}
                                                                            {user && user.phone && (
                                                                                <Typography variant="body2" color="text.secondary">{user.phone}</Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                    <Divider sx={{ my: 1 }} />
                                                                    <Box sx={{ mb: 1 }}>
                                                                        <Typography variant="body2" color="text.primary" fontWeight={500}>
                                                                            {pr.currency && pr.amount !== undefined ? `${pr.currency} ${Number(pr.amount/100).toFixed(2)}` : ''}
                                                                        </Typography>
                                                                        {user && user.stripeAccountId && (
                                                                            <Box sx={{ mt: 1, textAlign: 'left' }}>
                                                                                <Typography variant="body2" color="info.main">
                                                                                    Stripe Account: {user.stripeAccountId}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        <Typography variant="body2" sx={{ mt: 1 }}>Requested At: {formatRequestedAt(pr.requestedAt)}</Typography>
                                                                    </Box>
                                                                    <Box sx={{ mt: 'auto', textAlign: 'right' }}>
                                                                        <LoadingButton
                                                                            variant="contained"
                                                                            color="primary"
                                                                            loading={processingId === pr.id}
                                                                            onClick={() => handleProcessRequest(pr.id)}
                                                                            disabled={processedRequests.includes(pr.id)}
                                                                        >
                                                                            Process
                                                                        </LoadingButton>
                                                                    </Box>
                                                                </Paper>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            )}
                                        </Paper>
                                    )}
                                    {showCompletedList && (
                                        <Paper elevation={2} sx={{ mt: 2, p: 2, borderRadius: 2 }}>
                                            <Typography variant="subtitle1" fontWeight={600} color="#388e3c" sx={{ mb: 1 }}>
                                                Completed Requests List
                                            </Typography>
                                            {completedRequestsList.length === 0 ? (
                                                <Typography variant="body2" color="text.secondary">No completed requests.</Typography>
                                            ) : (
                                                <Grid container spacing={2}>
                                                    {completedRequestsList.map((pr, idx) => {
                                                        const user = completedUsers[pr.userId];
                                                        return (
                                                            <Grid item xs={12} md={4} key={pr.userId + '-' + idx}>
                                                                <Paper elevation={3} sx={{ p: 2, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                                        {user && user.photoURL && (
                                                                            <Avatar src={user.photoURL} alt={user.displayName || user.email || pr.userId} sx={{ mr: 2 }} />
                                                                        )}
                                                                        <Box>
                                                                            <Typography variant="subtitle2" color="primary">{user ? (user.displayName || user.email || pr.userId) : `User: ${pr.userId}`}</Typography>
                                                                            {user && user.email && (
                                                                                <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                                                            )}
                                                                            {user && user.phone && (
                                                                                <Typography variant="body2" color="text.secondary">{user.phone}</Typography>
                                                                            )}
                                                                        </Box>
                                                                    </Box>
                                                                    <Divider sx={{ my: 1 }} />
                                                                    <Box sx={{ mb: 1 }}>
                                                                        <Typography variant="body2" color="text.primary" fontWeight={500}>
                                                                            {pr.currency && pr.amount !== undefined ? `${pr.currency} ${Number(pr.amount/100).toFixed(2)}` : ''}
                                                                        </Typography>
                                                                        {user && user.stripeAccountId && (
                                                                            <Box sx={{ mt: 1, textAlign: 'left' }}>
                                                                                <Typography variant="body2" color="info.main">
                                                                                    Stripe Account: {user.stripeAccountId}
                                                                                </Typography>
                                                                            </Box>
                                                                        )}
                                                                        <Typography variant="body2" sx={{ mt: 1 }}>Processed At: {formatRequestedAt(pr.processedAt)}</Typography>
                                                                    </Box>
                                                                </Paper>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            )}
                                        </Paper>
                                    )}
                                </>
                            )}
                            {admin == 'manager' && (
                                <Box sx={{height: '70vh', p: 10, alignContent: 'center', textAlign: 'center'}}>
                                    Business Co-operator Access Permission
                                </Box>
                            )}
                            {admin == '' && (
                                <>
                                    Access Permission is required.
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            </>
        ) : (
            <Box sx={{width: '100%', p: 5, textAlign:'center'}}><CircularProgress /></Box>
        )}
    </>)    
}
